import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import Analytics from '../utils/Analytics';
import LoadingScreen from '../screens/LoadingScreen';
import ActionButton from './ActionButton';
import logo from '../projects/emailConfirmation-0424/assets/logo@3x.png';
import '../projects/emailConfirmation-0424/screens/EmailConfirmation.css';
import {useLocation} from 'react-router-dom';
import {LocalizationHandler} from '../i18n/LocalizationHandler';
import {
  forgetSavedIdentityOnConfirmationPage,
  forgetSessionOnFreshVisit,
  getLinkOutLoadDiagnostics,
  getLinkOutSession,
  readPendingLinkOutToken,
  redeemPendingLinkOutToken,
  rememberLinkOutPath,
  takeSessionRestoredFromCookie,
} from '../utils/linkOutSession';

forgetSessionOnFreshVisit();

type GateState = 'redeeming' | 'ready' | 'unreachable';

function LinkOutUnreachableScreen({onTryAgain}: {onTryAgain: () => void}) {
  const {t} = useTranslation();
  return (
    <div className="safe-full-screen w-screen flex flex-col justify-center items-center text-white px-6 text-center">
      <img src={logo} alt={t('{{brand}}')} width={56} height={56} />
      <h1 className="text-2xl font-bold mt-6">{t('link_out_unreachable.title')}</h1>
      <p className="text-md mt-3 mb-8 opacity-80">{t('link_out_unreachable.body')}</p>
      <div className="w-full max-w-sm">
        <ActionButton title={t('link_out_unreachable.try_again')} onClick={onTryAgain} />
      </div>
    </div>
  );
}

/**
 * Holds the routes back while a link-out token from the app is redeemed, so every screen's first render already sees
 * the person id and params (see src/utils/linkOutSession.ts). Pages opened without a token render immediately. While
 * it waits it shows the app's loading screen (the logo appears after 500ms, so a quick redeem shows only the black
 * background), and `Link Out Redeemed` / `Link Out Redeem Failed` record how long the person waited. When Hypnos
 * can't be reached the token is still unspent, so the gate offers to try again instead of rendering a page that
 * can't check out.
 */
const LinkOutGate: React.FC<{children: React.ReactNode}> = ({children}) => {
  const [state, setState] = useState<GateState>(() => (readPendingLinkOutToken() === null ? 'ready' : 'redeeming'));
  const [attempt, setAttempt] = useState(1);
  const {pathname} = useLocation();

  useEffect(() => {
    if (state === 'ready') {
      if (takeSessionRestoredFromCookie() && getLinkOutSession()) {
        Analytics.track('Link Out Session Restored', {
          source: 'cookie',
          path: pathname,
          ...getLinkOutLoadDiagnostics(),
        });
      }
      rememberLinkOutPath(pathname);
      forgetSavedIdentityOnConfirmationPage(pathname);
    }
  }, [state, pathname]);

  useEffect(() => {
    if (state !== 'redeeming') {
      return;
    }
    let cancelled = false;
    const waitStartedAt = Date.now();
    redeemPendingLinkOutToken().then(({outcome, reason, requestMs, early}) => {
      const timing = {wait_ms: Date.now() - waitStartedAt, request_ms: requestMs, early, attempt};
      if (outcome === 'redeemed') {
        Analytics.track('Link Out Redeemed', timing);
      } else if (outcome === 'failed') {
        Analytics.track('Link Out Redeem Failed', {reason, ...timing});
      }
      if (!cancelled) {
        setState(outcome === 'failed' && (reason === 'timeout' || reason === 'network') ? 'unreachable' : 'ready');
      }
    });
    return () => {
      cancelled = true;
    };
  }, [state, attempt]);

  if (state === 'unreachable') {
    return (
      <LocalizationHandler>
        <LinkOutUnreachableScreen
          onTryAgain={() => {
            setAttempt(a => a + 1);
            setState('redeeming');
          }}
        />
      </LocalizationHandler>
    );
  }
  return state === 'ready' ? <>{children}</> : <LoadingScreen />;
};

export default LinkOutGate;
